import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import styles from './BrandMark.module.scss'

type BrandMarkProps = {
  /**
   * Слово рядом со знаком. По умолчанию — имя продукта (`ui.productName`, одно на все страницы); консоль
   * оператора подписывает себя своим. Высота знака и отступ — CSS-переменные `--brand-h` и `--brand-gap`
   * на любом предке: у каждой страницы свой размер, и на телефоне он меняется медиазапросом.
   */
  name?: ReactNode
  /** Раскладка связки на странице: перенос, обрезка, отступы. Не начертание слова — оно в `nameClassName`. */
  className?: string
  /** Начертание слова: размер, цвет, вес — у каждой страницы своё. */
  nameClassName?: string
}

/**
 * ЗНАК INTCH И ИМЯ ПРОДУКТА НА ОДНОЙ БАЗОВОЙ ЛИНИИ — одна связка на все страницы (Дима, 2026-10-12: «сделай,
 * чтобы Connect был на одном уровне с Intch, а то он везде плавает по высоте, на всех страницах»).
 *
 * Плавал он потому, что каждая страница собирала связку сама: одни прижимали слово к низу картинки
 * (`align-items: flex-end`), другие ставили его на базовую линию картинки (`baseline`), шапка визарда
 * сдвигала на 2px вниз. Но низ картинки — не низ букв «Intch»: буквы кончаются на 39,47 из 42 единиц высоты
 * знака, ниже — только хвост значка. Поэтому слово садилось на 2,5 единицы ниже букв или висело выше —
 * по-разному при разных размерах знака и шрифта.
 *
 * Здесь картинка лежит в коробке, которая кончается ровно по низу букв (отрицательное поле в BrandMark.module.scss),
 * и слово выравнивается по базовой линии на эту коробку — при любой высоте знака и любом шрифте слова.
 */
export const BrandMark = ({ name, className, nameClassName }: BrandMarkProps) => {
  const { t } = useTranslation()
  const word = name === undefined ? t('ui.productName') : name

  return (
    <span className={cn(styles.root, className)}>
      <span className={styles.glyph}>
        <img src="/intch-logo.svg" alt="Intch" width={121} height={42} />
      </span>
      {word ? <span className={cn(styles.name, nameClassName)}>{word}</span> : null}
    </span>
  )
}
